import { useRef } from "react";
import {
  compactInputClass,
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingModelsPriceChangeFilters } from "./ManufacturingModelsPriceChangeFilters";
import { ManufacturingModelsPriceChangeTable } from "./ManufacturingModelsPriceChangeTable";
import { useManufacturingModelsPriceChange } from "./useManufacturingModelsPriceChange";

export function ManufacturingModelsPriceChangePanel({
  onClose,
  onCommitted,
}: {
  onClose: () => void;
  onCommitted?: (applied: number) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const p = useManufacturingModelsPriceChange(onCommitted);
  const drafts = p.summaries.filter((row) => row.status === "draft");
  const history = p.summaries.filter((row) => row.status === "committed");

  return (
    <section className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-surface-border-strong dark:bg-surface-raised">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-surface-ink">Price change</h2>
          <p className="text-xs text-gray-600 dark:text-surface-muted">
            Products, options, and packages. Fill only the new prices you want to change. COGS suggestions stay on the model until you commit.
          </p>
        </div>
        <button type="button" className={ghostBtnClass} onClick={onClose}>
          Back to models
        </button>
      </div>

      {p.error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {p.error}
        </div>
      ) : null}
      {p.notice ? <p className="text-xs text-gray-600 dark:text-surface-muted">{p.notice}</p> : null}
      {p.locked ? (
        <p className="text-sm text-gray-800 dark:text-surface-ink">
          Committed {p.committedAt ? p.committedAt.slice(0, 10) : ""}. This copy stays in history. Download the PDF or CSV to review it.
        </p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-[1fr_220px]">
        <label className="block text-xs font-medium text-gray-700 dark:text-surface-muted">
          Name
          <input
            className={`${inputClass} mt-1`}
            value={p.name}
            disabled={p.locked}
            placeholder="Spring 2027 wholesale"
            onChange={(event) => p.setName(event.target.value)}
          />
        </label>
        <label className="block text-xs font-medium text-gray-700 dark:text-surface-muted">
          Open saved
          <select
            className={`${inputClass} mt-1`}
            value={p.snapshotId ?? ""}
            onChange={(event) => void p.openSaved(event.target.value)}
          >
            <option value="">Working copy</option>
            {drafts.length ? (
              <optgroup label="Drafts">
                {drafts.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name} ({row.edit_count} edits)
                  </option>
                ))}
              </optgroup>
            ) : null}
            {history.length ? (
              <optgroup label="History">
                {history.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                    {row.committed_at ? ` · ${row.committed_at.slice(0, 10)}` : ""}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </label>
      </div>

      <label className="block text-xs font-medium text-gray-700 dark:text-surface-muted">
        Notes
        <textarea
          className={`${inputClass} mt-1 min-h-20`}
          value={p.notes}
          disabled={p.locked}
          placeholder="Who reviewed this, what the program is for, anything to remember later."
          onChange={(event) => p.setNotes(event.target.value)}
        />
      </label>

      <div className="flex flex-wrap gap-2">
        <button type="button" className={mfgPrimaryBtnClass} disabled={p.busy || p.locked} onClick={() => void p.save()}>
          Save
        </button>
        <button type="button" className={ghostBtnClass} disabled={p.busy} onClick={() => void p.startNew()}>
          New program
        </button>
        <select
          className={`${compactInputClass} w-40 text-xs`}
          value=""
          aria-label="Download or import"
          onChange={(event) => {
            const action = event.target.value;
            if (action === "csv") p.downloadCsv();
            if (action === "json") p.downloadJson();
            if (action === "pdf") p.downloadPdf();
            if (action === "import") fileRef.current?.click();
          }}
        >
          <option value="">Download / import</option>
          <option value="csv">Download CSV</option>
          <option value="json">Download JSON</option>
          <option value="pdf">Download PDF</option>
          <option value="import" disabled={p.locked}>
            Import CSV or JSON
          </option>
        </select>
        <span className="inline-flex items-center px-1 text-sm text-gray-800 dark:text-surface-ink">
          {p.confirmedCount}/{p.confirmTotal} confirmed
        </span>
        <button type="button" className={mfgPrimaryBtnClass} disabled={p.busy || p.locked} onClick={() => void p.commit()}>
          Commit {p.editCount} prices
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.json,text/csv,application/json"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void p.importFile(file);
          }}
        />
      </div>

      <ManufacturingModelsPriceChangeFilters
        search={p.search}
        setSearch={p.setSearch}
        group={p.group}
        setGroup={p.setGroup}
        groups={p.groups}
        roof={p.roof}
        setRoof={p.setRoof}
        roofs={p.roofs}
        kinds={p.kinds}
        narrow={p.narrow}
        setNarrowField={p.setNarrowField}
      />
      <p className="text-xs text-gray-500 dark:text-surface-muted">
        CSV and JSON download the filtered rows so you can edit a group or style and import the new prices back. Blank new prices in the file are left alone. A yes in the confirmation columns keeps that wholesale or MSRP marked reviewed. The PDF is the whole named program, including these notes.
      </p>

      <ManufacturingModelsPriceChangeTable
        lines={p.pageLines}
        locked={p.locked}
        page={p.page}
        pageCount={p.pageCount}
        filteredCount={p.filteredCount}
        onPage={p.setPage}
        onNewPrice={p.setNewPrice}
        onConfirm={p.setConfirmed}
      />
    </section>
  );
}
